import { FieldLabel } from 'src/components/Field';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Button,
  ButtonText,
  FormFeedback,
  Input,
  useToast
} from '@morressier/alembic';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getUserVerifyEmail,
  getVerifyExpireLink,
  useRegisterUser
} from '../../api/signUpMutation';
import { BrandLogo } from '../SignUpInviteSent';
import { useAuthRealm } from 'src/routes/AuthRealmAccessControl/AuthRealmContext';
import { replaceRedirectUrl } from 'src/pages/SignIn/ResetPassword';
import CopyrightFooterSection from 'src/components/User/CopyrightFooterSection';
import { useRef, useState } from 'react';

export type ISignUpUser = {
  email: string;
};

export const validateEmail =
  /^(?!\.)(?!.*\.\.)([a-z0-9_+%-]+(?:\.[a-z0-9_+%-]+)*)@([a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+)$/i;

export const SignUpForm = () => {
  const { loginUrl } = useAuthRealm();
  const toast = useToast();
  const signUpMutation = useRegisterUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const draftStepParam = searchParams.get('draftStep');
  const draftStep =
    draftStepParam === null ? NaN : Number(draftStepParam);
  const isResumeFlow =
    draftStepParam !== null && !Number.isNaN(draftStep);
  const email = searchParams.get('email') || '';
  const [isActivating, setIsActivating] = useState(false);
  // Synchronous guard against double-submit: the button's `disabled` state only
  // updates on the next render, so a fast double-click can otherwise fire two
  // concurrent `/users/register` requests (and two activation emails).
  const isSigningUp = useRef(false);

  const handleActivate = async () => {
    if (!token || isActivating) {
      return;
    }

    setIsActivating(true);

    try {
      // Validate the link first before verifying the email
      const validation = await getVerifyExpireLink(token);

      if (validation?.isExpired || !validation?.success) {
        toast({
          variant: 'error',
          title: 'Verification link error',
          description:
            validation?.message ||
            'This link is invalid or has expired. Please request a new link.',
          isClosable: true
        });
        navigate('/signup');
        return;
      }

      const data = await getUserVerifyEmail(token);
      const message = data?.message ? data.message : 'Internal Server Error';

      toast({
        variant: 'success',
        title: 'Email Verification Success',
        description: message,
        isClosable: true
      });
      navigate(
        `/signup/${data?.data?.id}?token=${token}${
          isResumeFlow ? `&draftStep=${draftStep}` : ''
        }`
      );
    } catch (error) {
      const message =
        (error as any)?.response?.data?.message || 'Something went wrong';
      toast({
        variant: 'error',
        title: 'Verification link error',
        description: message,
        isClosable: true
      });
      navigate('/signup');
    } finally {
      setIsActivating(false);
    }
  };

  const methods = useForm<ISignUpUser>({
    defaultValues: {
      email: email ?? ''
    }
  });

  const {
    handleSubmit,
    trigger,
    getValues,
    register,
    formState: { errors }
  } = methods;

  const handleSignUp = async () => {
    if (isSigningUp.current) {
      return;
    }
    isSigningUp.current = true;

    try {
      const values = getValues();

      const response = await signUpMutation.mutateAsync(values);

      if (response.isSuccess) {
        if (response.data) {
          toast({
            variant: 'success',
            title: 'Sign up invite sent',
            description: response?.message,
            isClosable: true
          });
          navigate(`/signup/invite-sent/${values.email}?status=draft`);
        } else {
          toast({
            variant: 'success',
            title: 'Invitation sent',
            description: response?.message,
            isClosable: true
          });
          navigate(`/signup/invite-sent/${values.email}`);
        }
      } else {
        const message = response?.message?.details?.length
          ? response.message.details[0]?.message
          : response?.message?.message || 'Internal Server Error';

        toast({
          variant: 'error',
          title: 'Sign Up Error',
          description: message,
          isClosable: true
        });
      }
    } finally {
      isSigningUp.current = false;
    }
  };

  const handleLogin = () => {
    const newLoginUrl = replaceRedirectUrl(loginUrl, window.location.origin);
    window.location.href = newLoginUrl;
  };

  const handleForgetPassword = () => {
    navigate('/signin/forgotpassword');
  };

  return (
    <Flex
      height="100%"
      flexGrow={2}
      flexBasis={0}
      minHeight={'100%'}
      direction="column"
    >
      <Flex
        flexDirection="column"
        gap={12}
        width={{ base: '95%', md: '80%' }}
        marginX={'auto'}
        flex={1}
      >
        <Flex justifyContent={'space-between'} alignItems={'end'}>
          <BrandLogo />
        </Flex>

        {token ? (
          <Flex flex="1" alignItems="center" justifyContent="center" px={4}>
            <Box textAlign="center" maxW="640px" width="full">
              <Text textStyle="headingGroup">
                {isResumeFlow ? 'Resume Signup' : 'Verify your email address'}
              </Text>
              <Text textStyle="textBody" mt={2}>
                {isResumeFlow
                  ? 'Continue where you left off by resuming your saved signup draft.'
                  : "we will unlock your signup form once your email is confirmed."}
              </Text>
              <Button onClick={handleActivate} my={8} disabled={isActivating}>
                {isActivating
                  ? isResumeFlow
                    ? 'Resuming...'
                    : 'Verifying...'
                  : isResumeFlow
                    ? 'Resume Signup'
                    : 'Verify Email'}
              </Button>
            </Box>
          </Flex>
        ) : (
          <Flex flexDirection={'column'} gap={4}>
            <Flex direction={'column'}>
              <Text textStyle="headingGroup">Journal Manager - Sign Up</Text>
              <Text textStyle="textBody">
                Join Journal Manager to become part of tomorrow's scientific
                breakthroughs.
              </Text>
              <FormProvider {...methods}>
                <form autoComplete="off" onSubmit={handleSubmit(handleSignUp)}>
                  <Flex gap={2} direction="column" mt={7}>
                    <FieldLabel
                      label="Email Address"
                      subText="You will receive an email with a link and instructions to activate your account."
                      isRequired
                    />
                    <Input
                      placeholder="Enter your email address"
                      {...register('email', {
                        required: 'Email address is required',
                        maxLength: {
                          value: 128,
                          message:
                            'Email address must not exceed 128 characters'
                        },
                        pattern: {
                          value: validateEmail,
                          message: 'Please enter a valid email address'
                        }
                      })}
                      onBlur={() => trigger('email')}
                      isInvalid={!!errors.email}
                      autoComplete="off"
                      aria-label="sign-up-email"
                    />
                    {errors.email && (
                      <FormFeedback variant="error">
                        {errors.email?.message}
                      </FormFeedback>
                    )}

                    <Button
                      type="submit"
                      my={3}
                      disabled={signUpMutation?.isLoading}
                    >
                      Sign Up
                    </Button>
                  </Flex>
                </form>
                <Flex justifyContent={'center'} gap={1}>
                  <Text textStyle={'headingItem'}>
                    {' '}
                    Already have an account?
                  </Text>
                  <ButtonText onClick={handleLogin}>Sign In</ButtonText>
                </Flex>
                <Flex justifyContent={'center'}>
                  <ButtonText
                    onClick={handleForgetPassword}
                    width={'fit-content'}
                  >
                    Forgot Password?
                  </ButtonText>
                </Flex>
              </FormProvider>
            </Flex>
          </Flex>
        )}
      </Flex>
      {!token && <CopyrightFooterSection />}
    </Flex>
  );
};
